/**
 * AI chat layout dimensions (Issue #12946)
 *
 * The docked sidecar's width, in one place, because three components have to
 * agree on it: `AiChatPanel` sizes itself with it, `AppFrame` reserves a grid
 * track of exactly it, and `AiChatFab` shifts left by it so it does not sit on
 * top of the panel.
 *
 * The panel's own width still has to be written as a Tailwind literal
 * (`sm:w-[440px]`) — Tailwind scans source text and cannot see through a
 * constant — so `ai-chat-panel-sidecar-dock.test.tsx` asserts that the class
 * string and this number stay the same number.
 *
 * This lives in its own module rather than in `ai-chat-panel.tsx` so the FAB
 * and the frame can import the dimension without pulling in the panel's
 * streaming machinery.
 */
import { APP_PANE_BREAKPOINTS } from '@/components/app-frame/app-pane-breakpoints'

export const AI_CHAT_SIDECAR_WIDTH_PX = 440

/**
 * The narrowest browser viewport that may dock the sidecar.
 *
 * Docking takes a fixed 440px out of a `minmax(0, 1fr)` pane that has no
 * floor, so on a narrow desktop the pane is whatever is left. The mobile
 * breakpoint (640px) is the wrong gate for that: a 720px viewport — a
 * half-screen window on a 1440p monitor, which is ordinary — is not mobile,
 * so it docked and left a 280px pane rendering a full desktop layout.
 *
 * 1080 is derived, not chosen: `1080 - 440 = 640`, exactly Tailwind's `sm`.
 * At or above it the pane is still at least a phone-width layout box, which
 * is the narrowest thing any route is built to render. Below it the panel
 * reserves nothing and stays the `sm:w-[440px]` fixed overlay it was before
 * the frame existed — the same state a chat-disabled org, a hidden route and
 * every mobile viewport already render, so this is a fallback to the status
 * quo rather than a fourth behavior.
 *
 * Issue #12946, review finding 1.
 */
export const AI_CHAT_DOCK_MIN_VIEWPORT_PX = 1080

/**
 * Viewport width where a docked chat still leaves the app pane at `pane-md`.
 * Below this (but above the dock threshold), both headers use their compact
 * height because 440px has been reserved from the application pane.
 */
export const AI_CHAT_TALL_HEADER_MIN_VIEWPORT_PX =
  AI_CHAT_SIDECAR_WIDTH_PX + APP_PANE_BREAKPOINTS.md

/** Duration shared by the panel slide and the app-frame track resize. */
export const AI_CHAT_TRANSITION_MS = 200

/**
 * How much of the BOTTOM of the browser viewport `AiChatFab` occupies, in px
 * (Issue #13731).
 *
 * `bottom-6` (24px) + the legacy FAB's `h-12` (48px) + an 8px gap = 80. The
 * SchoolAI treatment is shorter (a 24px dot plus `py-2`, about 42px; 44px on
 * a touch screen via `pointer-coarse:min-h-11`), so one number covers both
 * and errs toward clearing.
 *
 * 80 is not a new number: it is exactly what `AI_FAB_CLEARANCE_CLASS`
 * (`pr-20`) already reserves on the horizontal axis, so the two axes stay ONE
 * dimension. `ai-chat-fab-bottom-clearance-13731.test.tsx` asserts the
 * arithmetic between them, because Tailwind has to keep its half as a scanned
 * literal.
 *
 * The number itself never reaches a scrollport. `AiChatFab` reserves it
 * through `useFabReservation`, `AppFrame` publishes the reservation as
 * `--ai-fab-clearance`, and a scrollport consumes it with the
 * `.ai-fab-clear-bottom` class — which falls back to `0px`, so a route with no
 * frame above it and a route where the FAB is suppressed both render the
 * pre-#13731 layout by construction. Contract: docs/conventions/APP_FRAME.md.
 */
export const AI_FAB_CLEARANCE_PX = 80

/**
 * Tailwind right-padding class that reserves HORIZONTAL space for the
 * `AiChatFab` (48px wide + 24px from the right edge, rounded to the same 80px
 * the vertical half reserves). Apply it to any viewport-pinned toolbar that
 * sits at the bottom of the screen so its rightmost controls are not visually
 * overlapped by the FAB.
 *
 * Re-homed here from `ai-chat-fab.tsx` by #13731. It lived on the FAB because
 * the FAB is the source of the dimension (#7788 / PR #7789 review #1); that is
 * still true, and this module is where the FAB's dimensions live — next to
 * `AI_FAB_CLEARANCE_PX`, which is the same dimension on the other axis. Its
 * only consumer is `src/components/planning/plan-hierarchy-view.tsx`, and the
 * move also spares that module an import of the FAB component itself.
 *
 * Stays a literal because Tailwind scans source text and cannot see through a
 * constant.
 */
export const AI_FAB_CLEARANCE_CLASS = 'pr-20'
